import { useCallback, useRef, useState } from 'react';
import { notifications } from '@mantine/notifications';
import type { ReportView, SessionFilterOption, ZReportLayoutConfig } from 'shared-models/browser';
import { reportsService } from '../../../services/reports/reports.service';
import { pastReceiptsService } from '../../../services/past-receipts/past-receipts.service';
import { toHebrewApiErrorMessage } from '../../../services/index/index.service';

type ReportTileKey = 'reportX' | 'restoreZ' | 'workerReport' | 'itemsReport' | 'zcreditTransactions' | 'zReportLayout';

type UseReportsParams = {
  tenantId: string;
  employeeSession: string | null;
};

// Summary: POS reports controller — opens X live, the shared range modal (Z/worker), and any prebuilt report.
// Details: docs/function-library/web/contexts-pages.md#pages-posshellpage-hooks-usereports
export function useReports({ tenantId, employeeSession }: UseReportsParams) {
  const [singleReport, setSingleReport] = useState<ReportView | null>(null);
  const [singleReportOpen, setSingleReportOpen] = useState(false);
  const [singleReportTitle, setSingleReportTitle] = useState('דוח');
  const [loadingReport, setLoadingReport] = useState(false);
  const [rangeMode, setRangeMode] = useState<null | 'restoreZ' | 'worker'>(null);
  const [itemsReportOpen, setItemsReportOpen] = useState(false);
  const [zcreditSessionPickerOpen, setZcreditSessionPickerOpen] = useState(false);
  const [zcreditSessionOptions, setZcreditSessionOptions] = useState<SessionFilterOption[]>([]);
  const [zcreditSessionsLoading, setZcreditSessionsLoading] = useState(false);
  const [zcreditSelectedSession, setZcreditSelectedSession] = useState<{ id: number; name: string } | null>(null);
  const [zLayoutOpen, setZLayoutOpen] = useState(false);
  const [zLayoutLoading, setZLayoutLoading] = useState(false);
  const [zLayoutSaving, setZLayoutSaving] = useState(false);
  const [zLayoutConfig, setZLayoutConfig] = useState<ZReportLayoutConfig | null>(null);
  const zLayoutInFlightRef = useRef(false);
  const inFlightRef = useRef(false);

  const showError = useCallback((error: unknown) => {
    if (error instanceof Error) {
      notifications.show({ color: 'red', title: 'שגיאה', message: toHebrewApiErrorMessage(error.message), autoClose: 3000 });
    }
  }, []);

  const openX = useCallback(async () => {
    if (!employeeSession || inFlightRef.current) {
      return;
    }
    inFlightRef.current = true;
    setSingleReportTitle('דוח X');
    setSingleReport(null);
    setSingleReportOpen(true);
    setLoadingReport(true);
    try {
      const data = await reportsService.getX(tenantId, employeeSession);
      setSingleReport(data);
    } catch (error: unknown) {
      setSingleReportOpen(false);
      showError(error);
    } finally {
      setLoadingReport(false);
      inFlightRef.current = false;
    }
  }, [tenantId, employeeSession, showError]);

  const loadZcreditSessionOptions = useCallback(async () => {
    setZcreditSessionsLoading(true);
    try {
      const options = await pastReceiptsService.listDashboardSessionFilterOptions(tenantId);
      setZcreditSessionOptions(options);
    } catch {
      setZcreditSessionOptions([]);
    } finally {
      setZcreditSessionsLoading(false);
    }
  }, [tenantId]);

  const openZLayout = useCallback(async () => {
    if (!employeeSession || zLayoutInFlightRef.current) {
      return;
    }
    zLayoutInFlightRef.current = true;
    setZLayoutOpen(true);
    setZLayoutLoading(true);
    try {
      const response = await reportsService.getZLayoutConfig(tenantId, employeeSession);
      setZLayoutConfig(response.zReportLayoutConfig ?? null);
    } catch (error: unknown) {
      setZLayoutOpen(false);
      showError(error);
    } finally {
      setZLayoutLoading(false);
      zLayoutInFlightRef.current = false;
    }
  }, [employeeSession, showError, tenantId]);

  const saveZLayout = useCallback(async (nextConfig: ZReportLayoutConfig | null) => {
    if (!employeeSession || zLayoutSaving) {
      return false;
    }
    setZLayoutSaving(true);
    try {
      await reportsService.updateZLayoutConfig(tenantId, { zReportLayoutConfig: nextConfig }, employeeSession);
      setZLayoutConfig(nextConfig);
      setZLayoutOpen(false);
      return true;
    } catch (error: unknown) {
      showError(error);
      return false;
    } finally {
      setZLayoutSaving(false);
    }
  }, [employeeSession, showError, tenantId, zLayoutSaving]);

  const handleOpenReport = useCallback((key: ReportTileKey) => {
    if (key === 'reportX') {
      void openX();
    } else if (key === 'restoreZ') {
      setRangeMode('restoreZ');
    } else if (key === 'workerReport') {
      setRangeMode('worker');
    } else if (key === 'itemsReport') {
      setItemsReportOpen(true);
    } else if (key === 'zReportLayout') {
      void openZLayout();
    } else {
      setZcreditSessionPickerOpen(true);
      void loadZcreditSessionOptions();
    }
  }, [openX, openZLayout, loadZcreditSessionOptions]);

  const closeZcreditSessionPicker = useCallback(() => setZcreditSessionPickerOpen(false), []);
  const closeZLayout = useCallback(() => {
    if (zLayoutSaving) {
      return;
    }
    setZLayoutOpen(false);
  }, [zLayoutSaving]);

  const pickZcreditSession = useCallback((session: { id: number; name: string }) => {
    setZcreditSessionPickerOpen(false);
    setZcreditSelectedSession(session);
  }, []);

  const closeZcreditTransactions = useCallback(() => setZcreditSelectedSession(null), []);

  const backToZcreditSessionPicker = useCallback(() => {
    setZcreditSelectedSession(null);
    setZcreditSessionPickerOpen(true);
  }, []);

  // Open an already-built report (Z close preview, restore result) directly in the report modal.
  const openReportView = useCallback((report: ReportView, title: string) => {
    setSingleReport(report);
    setSingleReportTitle(title);
    setLoadingReport(false);
    setSingleReportOpen(true);
  }, []);

  const closeSingleReport = useCallback(() => setSingleReportOpen(false), []);
  const closeRangeModal = useCallback(() => setRangeMode(null), []);
  const closeItemsReport = useCallback(() => setItemsReportOpen(false), []);

  return {
    singleReport,
    singleReportOpen,
    singleReportTitle,
    loadingReport,
    handleOpenReport,
    openReportView,
    closeSingleReport,
    rangeMode,
    closeRangeModal,
    itemsReportOpen,
    closeItemsReport,
    zcreditSessionPickerOpen,
    zcreditSessionOptions,
    zcreditSessionsLoading,
    closeZcreditSessionPicker,
    zcreditSelectedSession,
    pickZcreditSession,
    closeZcreditTransactions,
    backToZcreditSessionPicker,
    zLayoutOpen,
    zLayoutLoading,
    zLayoutSaving,
    zLayoutConfig,
    closeZLayout,
    saveZLayout,
  };
}
